import React from 'react';

import { EeLoadingArea } from 'components/EeLoadingArea';
import { ApolloWrapper } from 'utils/ApolloWrapper';

import { Header } from '../components/Header';
import AccessCaptableContent from '../components/AccessCaptableContent';
import { Table } from 'components/table/Table';
import { MetricsGrid } from '../components/MetricsGrid';
import { TextSection } from '../components/TextSection';
import { getValue } from "../dashboardSettingsUtils";
import { CompanyInfo } from './CompanyInfo';
import { CompanyKPIs } from './CompanyKPIs';
import { CompanySelector } from './CompanySelector';
import { FundRaisingSummary } from './fundRaisingSummary';
import { SETTINGS_MAP } from './constants';
import { TopHolders } from './topHolders';
import { useCompany } from './useCompany';


export function Company() {
  const logic = useCompany();

  function mapNameToComponent(name: string, i: number) {
    if (!logic.company || !logic.mappedSettings) return <div key={i} />;

    const section = logic.mappedSettings.sections.find(s => s.name === name);
    if (!section) throw new Error(`Section ${name} not found in mapped settings`);

    if (name !== 'companyDetails' && !logic.company.investmentsStatus) return;

    switch (name) {
      case 'companyDetails':
        return <CompanyInfo company={logic.company} section={section} key={i} />;
      case 'description':
        if (!logic.company.description) return;
        return <TextSection key={i} title={'Description'} description={logic.company.description} />;
      case 'ownershipMetrics':
        return (
          <MetricsGrid
            title={'Ownership Metrics'}
            description={
              logic.company.useManualValuations
                ? `The NAV and EV data below (and related calculated values such as IRR and Multiple)
                are based on the latest manually entered values. To use Equity for Investors calculated values,
                select “Diligent Equity Calculated Values” in the Company Details page.`
                : ''
            }
            key={i}
            items={
              section.components
                .filter(c => c.checked)
                .map(c => ({
                  ...c,
                  value: getValue(
                    c,
                    logic.company,
                    'N/A',
                    window.GLOBAL_CONFIG.CURRENCIES[logic.company?.currency || ''],
                  ),
                  highlight: !!(logic.company?.useManualValuations && ['nav', 'ev', 'minEv', 'maxEv'].includes(c.name)),
                }))
            }
          />
        );
      case 'companyUpdate':
        if (!logic.company.update) return;
        return <TextSection
          key={i}
          description={logic.company.update}
          title={'Company Update'}
          tooltip={'Additional information for the company. Can be edited at Company details under Action for the selected company'}
        />;
      case 'sentiment':
        if (!logic.company.sentiment) return;
        return <TextSection key={i} title={'Quarterly Sentiment'} description={logic.company.sentiment} />;
      case 'fundRaisingSummary':
        return <FundRaisingSummary key={i} company={logic.company} section={section} />;
      case 'topInvestors':
        if (!logic.company.topHolders.length) return;
        return <TopHolders company={logic.company} holderName={logic.fundName} key={i} />;
      case 'kpis':
        return (
          <ApolloWrapper key={i} graphqlUrl={window.GLOBAL_CONFIG.API_GATEWAY_GRAPHQL_URL}>
            <CompanyKPIs key={i} companyId={logic.company.id} />
          </ApolloWrapper>
        );

      case 'clonedVersions':
        const clonedVersions = logic?.company?.clonedVersions;
        return (
          clonedVersions !== null && (
            <Table
              columns={[
                {
                  label: 'Name',
                },
                {
                  label: 'Cap Table',
                  nonSortable: true,
                  cellProps: {
                    style: { width: '50%' },
                  },
                },
              ]}
              key={i}
              rows={(() => {
                let result = [] as any[];
                clonedVersions?.forEach((c: any) => {
                  result.push([
                    {
                      content: c.clonedCapTableName,
                      sortingValue: c.clonedCapTableName,
                    },
                    {
                      content: (
                        <AccessCaptableContent
                          captableLink={c.clonedCapTableUrl}
                        />
                      ),
                    },
                  ]);
                });
                return result;
              })()}
              sectionTitle="Cloned Versions"
            />
          )
        );

      default:
        console.log(`Section ${name} is not implemented`)
    }
  }

  const getDashboardSettingsMap = (logic: any) => {
    let mapppedSettings = { ...SETTINGS_MAP };

    if (logic?.company?.clonedVersions === null) {
      delete mapppedSettings['clonedVersions'];
    }

    return mapppedSettings;
  };

  if (logic.hasNoDefaultCompany) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center lens-h-80vh">
        <div className="text-center text-muted">
          <p className="mb-0">No company associated with your account.</p>
          <p className="mb-0 small">Contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (!logic.company) {
    if (logic.companyIsLoading) {
      return (
        <div className="vh-100 d-flex justify-content-center align-items-center lens-h-80vh">
          <EeLoadingArea />
        </div>
      );
    }

    return (
      <div className="vh-100 d-flex justify-content-center align-items-center lens-h-80vh">
        <div className="text-center text-muted">
          <p className="mb-0">Company not found or you do not have access to this company.</p>
          <p className="mb-0 small">Contact your administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        confirmText={
          <>
            The changes you made will affect company {logic.company.name}.
            <br /> Are you sure to apply?
          </>
        }
        dashboardSettings={logic.company.dashboardSettings}
        description={
          <>
            Choose sections and components to be displayed in the Company
            Dashboard. <br /> Changes will apply to company {logic.company.name}.
          </>
        }
        defaultDashboardSettings={logic.company.defaultDashboardSettings}
        onSettingsClose={logic.onSettingsClose}
        saveDashboardSettings={logic.saveDashboardSettings}
        settingsMap={getDashboardSettingsMap(logic)}
        submitText="APPLY"
        title={logic.company.name}
      />
      <CompanySelector
        companies={logic.companies}
        company={logic.company}
        companyIsLoading={logic.companyIsLoading}
        reloadCompany={logic.reloadCompany}
      />
      {logic.companyIsLoading && <EeLoadingArea />}

      {logic.mappedSettings?.sections.map((section, i: number) => {
        return section.checked ? mapNameToComponent(section.name, i) : <div key={i} />;
      })}
    </>
  );
}
